import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Grid, Skeleton } from '@radix-ui/themes';
import { BuildPriorityCallout } from 'components/common/build-priority-callout';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { NoDataPlaceholder } from 'components/common/table/no-data-placeholder';
import { GameDataPlayerCard } from 'components/sections/game-data/players/card';
import { GameDataPlayersFilters } from 'components/sections/game-data/players/filters';
import { GameDataPitchersFooter } from 'components/sections/game-data/players/pitchers-footer';
import { GameDataContext } from 'contexts/game-data.context';
import { CheckAllButton } from 'contexts/layout/checked-context/select-all-button';
import { CheckedProvider } from 'contexts/layout/checked.context';
import { GameDataTab } from 'enums/game-data.enums';
import { t } from 'i18next';
import { ITableNoData } from 'interfaces/tables/no-data';
import { HITTER_SIDES, HitterSide } from 'lib_ts/enums/hitters.enums';
import { PITCHER_HANDS, PitcherHand } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo, useState } from 'react';
import slugify from 'slugify';
import { useCookiesStore } from 'stores/use-cookies-store';

const ITEMS_PER_PAGE = 100;

const getSearchTerm = (s: string) =>
  slugify(s, {
    lower: true,
    strict: true,
    replacement: ' ',
  });

export const GameDataPlayers = (props: { mode: GameDataTab }) => {
  const app = useCookiesStore((s) => s.app);

  const [pages, setPages] = useState(1);

  const [resetKey, setResetKey] = useState(Date.now());

  const [search, setSearch] = useState('');

  const safeSearch = useMemo(() => getSearchTerm(search), [search]);

  const { loading, seasonPlayers, filterPlayers, mergeFilterPlayers } =
    useContext(GameDataContext);

  const players = useMemo(() => {
    return seasonPlayers
      .filter((p) => {
        switch (props.mode) {
          case 'pitchers': {
            return p._mx_details?.isPitcher;
          }

          case 'hitters': {
            return p._mx_details?.isBatter;
          }

          // show nothing for any other mode
          default: {
            return false;
          }
        }
      })
      .filter((p) => {
        switch (props.mode) {
          case 'pitchers': {
            return !filterPlayers.hand || filterPlayers.hand === p.pitchHand;
          }

          case 'hitters': {
            return !filterPlayers.side || filterPlayers.side === p.batSide;
          }

          default: {
            return true;
          }
        }
      })
      .filter(
        (p) =>
          !filterPlayers.teamPk ||
          p?._mx_team_details?.teamPk === filterPlayers.teamPk
      )
      .filter(
        (p) =>
          safeSearch.length === 0 || getSearchTerm(p.name).includes(safeSearch)
      );
  }, [safeSearch, seasonPlayers, filterPlayers]);

  const noDataProps = useMemo(() => {
    switch (props.mode) {
      case 'hitters': {
        const output: ITableNoData = {
          noDataHeader: 'No Hitters to Display',
          noDataBody: 'No hitters match your search',
        };
        return output;
      }

      case 'pitchers': {
        const output: ITableNoData = {
          noDataHeader: 'No Pitchers to Display',
          noDataBody: 'No pitchers match your search',
        };
        return output;
      }

      default: {
        return undefined;
      }
    }
  }, [props.mode]);

  const dummyPlayers = useMemo(
    () => Array.from({ length: 24 }, (_, i) => i),
    []
  );

  const playerIDs = useMemo(() => players.map((p) => p._id), [players]);

  return (
    <ErrorBoundary identifier="GameDataPlayers">
      <CheckedProvider data={players}>
        <Flex
          direction="column"
          gap="3"
          style={{
            height: '100%',
          }}
        >
          <Flex gap={RADIX.FLEX.GAP.SM}>
            {props.mode === 'pitchers' && <CheckAllButton ids={playerIDs} />}

            <GameDataPlayersFilters
              onResetFilters={() => {
                setSearch('');
                setResetKey(Date.now());
              }}
            >
              <CommonTextInput
                id="player-search"
                value={search}
                onChange={(v) => setSearch(v ?? '')}
                leftSlot={<MagnifyingGlassIcon />}
                placeholder="common.search-by-name"
              />

              {props.mode === 'pitchers' && (
                <CommonSelectInput
                  key={`hand-${resetKey}`}
                  id="pitcher-hand"
                  placeholder="common.hand"
                  options={PITCHER_HANDS}
                  value={filterPlayers.hand}
                  onChange={(v) =>
                    mergeFilterPlayers({ hand: v as PitcherHand })
                  }
                  optional
                />
              )}

              {props.mode === 'hitters' && (
                <CommonSelectInput
                  key={`side-${resetKey}`}
                  id="batter-side"
                  placeholder="common.side"
                  options={HITTER_SIDES}
                  value={filterPlayers.side}
                  onChange={(v) =>
                    mergeFilterPlayers({ side: v as HitterSide })
                  }
                  optional
                />
              )}
            </GameDataPlayersFilters>
          </Flex>

          <Box>
            <BuildPriorityCallout
              priority={app.game_data_import_options.priority}
            />
          </Box>

          {!loading && players.length === 0 && (
            <NoDataPlaceholder {...noDataProps} />
          )}

          <Box
            flexGrow="1"
            style={{
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            <Grid columns={{ initial: '1', sm: '2', lg: '3' }} gap="3">
              {loading ? (
                dummyPlayers.map((_, i) => <Skeleton key={i} height="80px" />)
              ) : (
                <>
                  {players
                    .filter((_, i) => i < pages * ITEMS_PER_PAGE)
                    .map((p) => (
                      <GameDataPlayerCard
                        key={p.playerPk}
                        player={p}
                        mode={props.mode}
                      />
                    ))}

                  {players.length > pages * ITEMS_PER_PAGE && (
                    <Flex
                      justify="center"
                      gridColumn={{
                        initial: undefined,
                        md: 'span 2',
                        lg: 'span 3',
                      }}
                    >
                      <Button
                        onClick={() => setPages(pages + 1)}
                        variant={RADIX.BUTTON.VARIANT.SECONDARY}
                      >
                        {t('common.load-more')}
                      </Button>
                    </Flex>
                  )}
                </>
              )}
            </Grid>
          </Box>

          {props.mode === 'pitchers' && (
            <GameDataPitchersFooter pitchers={players} />
          )}
        </Flex>
      </CheckedProvider>
    </ErrorBoundary>
  );
};
